import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ticketAPI, complaintTypeAPI } from '../services/api';
import { FiArrowLeft, FiUpload, FiX } from 'react-icons/fi';
import './CreateTicket.css';

const CreateTicket = () => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    complaint_type_id: '',
  });
  const [files, setFiles] = useState([]);
  const [complaintTypes, setComplaintTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchComplaintTypes();
  }, []);

  const fetchComplaintTypes = async () => {
    try {
      const response = await complaintTypeAPI.getAll();
      setComplaintTypes(response.data);
    } catch (error) {
      console.error('Error fetching complaint types:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = selectedFiles.filter(file => {
      const isValidType = file.type.startsWith('image/') || file.type.startsWith('video/');
      const isValidSize = file.size <= 50 * 1024 * 1024; // 50MB
      return isValidType && isValidSize;
    });

    if (validFiles.length !== selectedFiles.length) {
      setError('Some files were rejected. Only images and videos under 50MB are allowed.');
    }

    setFiles([...files, ...validFiles]);
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('complaint_type_id', formData.complaint_type_id);
      formDataToSend.append('user_id', user.id);

      files.forEach((file) => {
        formDataToSend.append('attachments', file);
      });

      const response = await ticketAPI.createTicket(formDataToSend);
      setSuccess('Ticket created successfully!');
      
      setTimeout(() => {
        navigate(`/ticket/${response.data.ticket.id}`);
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create ticket. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="create-ticket-container">
      <div className="container">
        <div className="create-ticket-header">
          <button onClick={() => navigate('/dashboard')} className="back-button">
            <FiArrowLeft /> Back to Dashboard
          </button>
        </div>

        <div className="create-ticket-card card fade-in">
          <div className="form-header">
            <h1 className="form-title">Create New Ticket</h1>
            <p className="form-subtitle">Fill in the details below to submit your ticket</p>
          </div>

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          <form onSubmit={handleSubmit} className="ticket-form">
            <div className="form-group">
              <label className="form-label">Ticket Title *</label>
              <input
                type="text"
                name="title"
                className="form-input"
                value={formData.title}
                onChange={handleChange}
                placeholder="Brief description of your issue"
                required
                maxLength={500}
              />
              <span className="char-count">{formData.title.length}/500</span>
            </div>

            <div className="form-group">
              <label className="form-label">Complaint Type *</label>
              <select
                name="complaint_type_id"
                className="form-select"
                value={formData.complaint_type_id}
                onChange={handleChange}
                required
              >
                <option value="">Select a type</option>
                {complaintTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.type_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Description *</label>
              <textarea
                name="description"
                className="form-textarea"
                value={formData.description}
                onChange={handleChange}
                placeholder="Provide detailed information about your issue..."
                required
                rows={6}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Attachments (Optional)</label>
              <div className="upload-area">
                <input
                  type="file"
                  id="file-upload"
                  className="file-input"
                  onChange={handleFileChange}
                  accept="image/*,video/*"
                  multiple
                />
                <label htmlFor="file-upload" className="upload-label">
                  <FiUpload className="upload-icon" />
                  <span className="upload-text">Click to upload images or videos</span>
                  <span className="upload-hint">Max file size: 50MB • Max 5 files</span>
                </label>
              </div>

              {files.length > 0 && (
                <div className="files-list">
                  {files.map((file, index) => (
                    <div key={index} className="file-item">
                      <div className="file-info">
                        <span className="file-name">{file.name}</span>
                        <span className="file-size">{formatFileSize(file.size)}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="remove-file-btn"
                      >
                        <FiX />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="form-info">
              <div className="info-item">
                <span className="info-label">Created By:</span>
                <span className="info-value">{user.full_name}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Date:</span>
                <span className="info-value">{new Date().toLocaleString()}</span>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="btn btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Creating Ticket...' : 'Create Ticket'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateTicket;
